<template>
    <div class="bulletin-board-wrapper">
        <!-- Floating Button -->
        <div 
            class="bulletin-board-button" 
            @click="toggleBulletinBoard"
            :class="{ 'active': isOpen }"
        >
            <div class="bulletin-icon">
                📋
            </div>
            <div class="bulletin-text">
                B<br>U<br>L<br>L<br>E<br>T<br>I<br>N
            </div>
            <div v-if="unreadCount > 0" class="notification-badge">
                {{ unreadCount }}
            </div>
        </div>

        <!-- Bulletin Board Panel -->
        <div 
            class="bulletin-board-panel" 
            :class="{ 'open': isOpen }"
        >
            <div class="panel-header">
                <h3>📋 Bulletin Board</h3>
                <button @click="closeBulletinBoard" class="close-btn">✕</button>
            </div>

            <div class="panel-content">
                <!-- Post Input -->
                <div v-if="canPost" class="post-input-section">
                    <textarea 
                        v-model="newPost" 
                        placeholder="Share an announcement or message..."
                        class="post-input"
                        rows="3"
                    ></textarea>

                    <!-- Enhanced Controls Row -->
                    <div class="post-controls-row">
                        <!-- File Upload Section -->
                        <div class="control-group">
                            <input 
                                type="file" 
                                ref="fileInput" 
                                @change="handleFileSelect" 
                                multiple 
                                style="display: none"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                            >
                            <button 
                                @click="$refs.fileInput.click()" 
                                class="control-btn file-btn"
                                type="button"
                            >
                                📎 Files ({{ selectedFiles.length }})
                            </button>
                        </div>

                        <!-- Audience Multi-Select -->
                        <div v-if="canSelectAudience" class="control-group audience-group">
                            <div class="multiselect-wrapper">
                                <button 
                                    @click="showAudienceDropdown = !showAudienceDropdown"
                                    class="control-btn audience-btn"
                                    type="button"
                                >
                                    👥 {{ getAudienceText() }}
                                    <span class="dropdown-arrow">{{ showAudienceDropdown ? '▲' : '▼' }}</span>
                                </button>

                                <div v-if="showAudienceDropdown" class="multiselect-dropdown">
                                    <label class="checkbox-item">
                                        <input 
                                            type="checkbox" 
                                            :checked="targetRoles.includes('all')"
                                            @change="toggleRole('all')"
                                        >
                                        <span>All Users</span>
                                    </label>
                                    <label class="checkbox-item">
                                        <input 
                                            type="checkbox" 
                                            :checked="targetRoles.includes('admin')"
                                            @change="toggleRole('admin')"
                                        >
                                        <span>Admins</span>
                                    </label>
                                    <label class="checkbox-item">
                                        <input 
                                            type="checkbox" 
                                            :checked="targetRoles.includes('counselor')"
                                            @change="toggleRole('counselor')"
                                        >
                                        <span>Counselors</span>
                                    </label>
                                    <label class="checkbox-item">
                                        <input 
                                            type="checkbox" 
                                            :checked="targetRoles.includes('nurse')"
                                            @change="toggleRole('nurse')"
                                        >
                                        <span>Nurses</span>
                                    </label>
                                    <label class="checkbox-item">
                                        <input 
                                            type="checkbox" 
                                            :checked="targetRoles.includes('employee')"
                                            @change="toggleRole('employee')"
                                        >
                                        <span>Employees</span>
                                    </label>
                                    <label class="checkbox-item">
                                        <input 
                                            type="checkbox" 
                                            :checked="targetRoles.includes('frontdesk')"
                                            @change="toggleRole('frontdesk')"
                                        >
                                        <span>Front Desk</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Priority Selection -->
                        <div class="control-group">
                            <select v-model="postPriority" class="control-select priority-select">
                                <option value="normal">📝 Normal</option>
                                <option value="important" v-if="canPostImportant">⚠️ Important</option>
                                <option value="urgent" v-if="canPostUrgent">🚨 Urgent</option>
                            </select>
                        </div>

                        <!-- Post Button -->
                        <div class="control-group">
                            <button 
                                @click="createPost" 
                                class="post-btn"
                                :disabled="!newPost.trim() && !selectedFiles.length"
                            >
                                Post
                            </button>
                        </div>
                    </div>

                    <!-- Selected Files Display -->
                    <div v-if="selectedFiles.length" class="selected-files-display">
                        <h5>📎 Selected Files:</h5>
                        <div class="files-grid">
                            <div v-for="(file, index) in selectedFiles" :key="index" class="file-chip">
                                <span class="file-icon">{{ getFileIcon(file.name.split('.').pop()) }}</span>
                                <span class="file-name">{{ truncateFileName(file.name) }}</span>
                                <button @click="removeFile(index)" class="remove-chip-btn">✕</button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- No Permission Message -->
                <div v-else class="no-permission-message">
                    <p>📋 You don't have permission to create posts.</p>
                    <p><small>Only admins, counselors, and nurses can create bulletin board posts.</small></p>
                </div>

                <!-- Posts List -->
                <div class="posts-list">
                    <div 
                        v-for="post in posts" 
                        :key="post.id" 
                        class="post-item"
                        :class="{ 
                            'important': post.priority === 'important',
                            'urgent': post.priority === 'urgent',
                            'unread': !post.read
                        }"
                        @click="markAsRead(post)"
                    >
                        <div class="post-header">
                            <div class="post-author">
                                <span class="author-name">{{ post.author.name }}</span>
                                <span class="post-time">{{ formatTime(post.created_at) }}</span>
                            </div>
                            <div class="post-priority">
                                {{ getPriorityIcon(post.priority) }}
                            </div>
                        </div>
                        <div class="post-content">
                            {{ post.content }}
                        </div>

                        <!-- Attachments -->
                        <div v-if="post.attachments && post.attachments.length" class="post-attachments">
                            <h5>📎 Attachments:</h5>
                            <div class="attachment-list">
                                <div v-for="attachment in post.attachments" :key="attachment.id" class="attachment-item">
                                    <span class="attachment-icon">{{ getFileIcon(attachment.type) }}</span>
                                    <span class="attachment-name">{{ attachment.name }}</span>
                                    <div class="attachment-actions">
                                        <button @click.stop="downloadAttachment(attachment)" class="download-btn">⬇️</button>
                                        <button @click.stop="viewAttachment(attachment)" class="view-btn">👁️</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Target Roles -->
                        <div v-if="post.target_roles && post.target_roles.length" class="post-audience">
                            <small>👥 For: {{ formatTargetRoles(post.target_roles) }}</small>
                        </div>

                        <div class="post-footer">
                            <button 
                                v-if="canDelete(post)" 
                                @click.stop="deletePost(post.id)" 
                                class="delete-btn"
                            >
                                🗑️
                            </button>
                        </div>
                    </div>

                    <div v-if="posts.length === 0" class="no-posts">
                        <p>No announcements yet. Be the first to post!</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Overlay -->
        <div 
            v-if="isOpen" 
            class="bulletin-overlay" 
            @click="closeBulletinBoard"
        ></div>
    </div>
</template>

<script>
export default {
    name: 'BulletinBoard',
    data() {
        return {
            isOpen: false,
            newPost: '',
            postPriority: 'normal',
            posts: [],
            unreadCount: 0,
            currentUser: null,
            selectedFiles: [],
            targetRoles: ['all'],
            showAudienceDropdown: false
        };
    },
    computed: {
        canPost() {
            if (!this.currentUser) return false;
            const allowedRoles = ['admin', 'counselor', 'nurse'];
            return allowedRoles.includes(this.currentUser.role);
        },

        canPostImportant() {
            if (!this.currentUser) return false;
            const allowedRoles = ['admin', 'counselor'];
            return allowedRoles.includes(this.currentUser.role);
        },

        canPostUrgent() {
            if (!this.currentUser) return false;
            return this.currentUser.role === 'admin';
        },

        canSelectAudience() {
            if (!this.currentUser) return false;
            const allowedRoles = ['admin', 'counselor'];
            return allowedRoles.includes(this.currentUser.role);
        }
    },

    mounted() {
        this.currentUser = this.$store.getters.getUser;
        this.loadPosts();
        this.startPolling();
    },
    beforeUnmount() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }
    },
    methods: {
        toggleBulletinBoard() {
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                this.loadPosts();
            }
        },

        closeBulletinBoard() {
            this.isOpen = false;
        },

        async loadPosts() {
            try {
                const response = await this.$api.get('/bulletin-posts');
                this.posts = response.data;
                this.updateUnreadCount();
            } catch (error) {
                console.error('Error loading posts:', error);
            }
        },

        async createPost() {
            if (!this.newPost.trim() && !this.selectedFiles.length) return;

            try {
                const formData = new FormData();
                formData.append('content', this.newPost);
                formData.append('priority', this.postPriority);
                formData.append('target_roles', JSON.stringify(this.targetRoles));

                // Add files
                this.selectedFiles.forEach((file, index) => {
                    formData.append(`files[${index}]`, file);
                });

                await this.$api.post('/bulletin-posts', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                this.newPost = '';
                this.postPriority = 'normal';
                this.selectedFiles = [];
                this.targetRoles = ['all'];
                this.loadPosts();
                this.$toast.success('Post created successfully');
            } catch (error) {
                console.error('Error creating post:', error);
                this.$toast.error('Error creating post');
            }
        },

        handleFileSelect(event) {
            const files = Array.from(event.target.files);
            this.selectedFiles.push(...files);
        },

        removeFile(index) {
            this.selectedFiles.splice(index, 1);
        },

        async downloadAttachment(attachment) {
            try {
                const response = await this.$api.get(`/bulletin-posts/download/${attachment.id}`, {
                    responseType: 'blob'
                });

                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = attachment.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error downloading attachment:', error);
                this.$toast.error('Error downloading attachment');
            }
        },

        viewAttachment(attachment) {
            window.open(`http://127.0.0.1:8001/api/bulletin-posts/view/${attachment.id}`, '_blank');
        },

        getFileIcon(type) {
            const iconMap = {
                'pdf': '📄',
                'doc': '📝',
                'docx': '📝',
                'xls': '📊',
                'xlsx': '📊',
                'ppt': '📊',
                'pptx': '📊',
                'txt': '📄',
                'jpg': '🖼️',
                'jpeg': '🖼️',
                'png': '🖼️',
                'gif': '🖼️'
            };
            return iconMap[type] || '📄';
        },

        formatTargetRoles(roles) {
            if (roles.includes('all')) return 'All Users';
            return roles.map(role => {
                return role.charAt(0).toUpperCase() + role.slice(1);
            }).join(', ');
        },

        getAudienceText() {
            if (this.targetRoles.includes('all') || this.targetRoles.length === 0) {
                return 'All Users';
            }
            if (this.targetRoles.length === 1) {
                return this.targetRoles[0].charAt(0).toUpperCase() + this.targetRoles[0].slice(1);
            }
            return `${this.targetRoles.length} Roles`;
        },

        toggleRole(role) {
            if (role === 'all') {
                this.targetRoles = ['all'];
            } else {
                const index = this.targetRoles.indexOf(role);
                const allIndex = this.targetRoles.indexOf('all');

                // Remove 'all' if selecting specific roles
                if (allIndex !== -1) {
                    this.targetRoles.splice(allIndex, 1);
                }

                if (index === -1) {
                    this.targetRoles.push(role);
                } else {
                    this.targetRoles.splice(index, 1);
                }

                // If no roles selected, default to 'all'
                if (this.targetRoles.length === 0) {
                    this.targetRoles = ['all'];
                }
            }
        },

        truncateFileName(fileName) {
            if (fileName.length <= 20) return fileName;
            const ext = fileName.split('.').pop();
            const name = fileName.substring(0, fileName.lastIndexOf('.'));
            return `${name.substring(0, 15)}...${ext}`;
        },

        async markAsRead(post) {
            if (!post.read) {
                try {
                    await this.$api.put(`/bulletin-posts/${post.id}/read`);
                    post.read = true;
                    this.updateUnreadCount();
                } catch (error) {
                    console.error('Error marking post as read:', error);
                }
            }
        },

        async deletePost(postId) {
            if (confirm('Are you sure you want to delete this post?')) {
                try {
                    await this.$api.delete(`/bulletin-posts/${postId}`);
                    this.loadPosts();
                    this.$toast.success('Post deleted successfully');
                } catch (error) {
                    console.error('Error deleting post:', error);
                    this.$toast.error('Error deleting post');
                }
            }
        },

        updateUnreadCount() {
            this.unreadCount = this.posts.filter(post => !post.read).length;
        },

        canDelete(post) {
            return this.currentUser && 
                   (this.currentUser.id === post.author_id || 
                    this.currentUser.role === 'admin');
        },

        formatTime(datetime) {
            const date = new Date(datetime);
            const now = new Date();
            const diff = now - date;

            if (diff < 60000) return 'Just now';
            if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
            if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
            return date.toLocaleDateString();
        },

        getPriorityIcon(priority) {
            switch (priority) {
                case 'important': return '⚠️';
                case 'urgent': return '🚨';
                default: return '📝';
            }
        },

        startPolling() {
            this.pollingInterval = setInterval(() => {
                if (!this.isOpen) {
                    this.loadPosts();
                }
            }, 30000); // Poll every 30 seconds when closed
        }
    }
};
</script>

<style scoped>
.bulletin-board-wrapper {
    position: relative;
    z-index: 1000;
}

.bulletin-board-button {
    position: fixed;
    right: -15px;
    top: 50%;
    transform: translateY(-50%);
    background: linear-gradient(135deg, #1dbcbc, #159999);
    color: white;
    padding: 25px 15px;
    border-radius: 20px 0 0 20px;
    cursor: pointer;
    box-shadow: -3px 0 15px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    min-height: 200px;
}

.bulletin-board-button:hover {
    right: 0;
    box-shadow: -5px 0 25px rgba(0, 0, 0, 0.3);
    transform: translateY(-50%) scale(1.05);
}

.bulletin-board-button.active {
    right: 0;
    background: linear-gradient(135deg, #159999, #117a7a);
}

.bulletin-icon {
    font-size: 28px;
}

.bulletin-text {
    font-size: 14px;
    font-weight: bold;
    line-height: 1.2;
    text-align: center;
    letter-spacing: 1.5px;
}

.notification-badge {
    position: absolute;
    top: 10px;
    left: -8px;
    background: #ff4757;
    color: white;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    animation: pulse 2s infinite;
    border: 2px solid white;
}

@keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
}

.bulletin-board-panel {
    position: fixed;
    right: -400px;
    top: 0;
    width: 400px;
    height: 100vh;
    background: white;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.2);
    transition: right 0.3s ease;
    z-index: 1002;
    display: flex;
    flex-direction: column;
}

.bulletin-board-panel.open {
    right: 0;
}

.panel-header {
    background: linear-gradient(135deg, #1dbcbc, #159999);
    color: white;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.panel-header h3 {
    margin: 0;
    font-size: 1.2rem;
}

.close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    padding: 5px;
}

.close-btn:hover {
    opacity: 0.8;
}

.panel-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.post-input-section {
    margin-bottom: 20px;
    border-bottom: 1px solid #eee;
    padding-bottom: 20px;
}

.post-input {
    width: 100%;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    font-family: inherit;
    resize: vertical;
    min-height: 80px;
}

.post-input:focus {
    border-color: #1dbcbc;
    outline: none;
}

.post-controls-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    margin: 15px 0;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.control-group {
    position: relative;
}

.control-btn {
    background: white;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 13px;
    color: #495057;
    transition: all 0.2s;
    white-space: nowrap;
}

.control-btn:hover {
    border-color: #1dbcbc;
    color: #1dbcbc;
    background-color: #f0fffe;
}

.file-btn {
    background: linear-gradient(45deg, #f8f9fa, #e9ecef);
}

.audience-btn {
    min-width: 120px;
    text-align: left;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.dropdown-arrow {
    font-size: 10px;
    margin-left: 5px;
}

.multiselect-wrapper {
    position: relative;
}

.multiselect-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-height: 200px;
    overflow-y: auto;
}

.checkbox-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 13px;
    transition: background-color 0.2s;
}

.checkbox-item:hover {
    background-color: #f8f9fa;
}

.checkbox-item input {
    margin-right: 8px;
    transform: scale(0.9);
}

.control-select {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13px;
    background: white;
    min-width: 100px;
}

.control-select:focus {
    border-color: #1dbcbc;
    outline: none;
}

.selected-files-display {
    margin: 10px 0;
    padding: 12px;
    background: #f0fffe;
    border-radius: 6px;
    border: 1px solid #b3e5e5;
}

.selected-files-display h5 {
    margin: 0 0 8px 0;
    font-size: 13px;
    color: #495057;
}

.files-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.file-chip {
    display: flex;
    align-items: center;
    background: white;
    border: 1px solid #1dbcbc;
    border-radius: 15px;
    padding: 4px 8px;
    font-size: 12px;
    gap: 5px;
}

.file-icon {
    font-size: 14px;
}

.file-name {
    color: #495057;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.remove-chip-btn {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    font-size: 12px;
    padding: 0;
    margin-left: 3px;
}

.remove-chip-btn:hover {
    color: #c82333;
}

.audience-group {
    flex-grow: 1;
    min-width: 120px;
}

@media (max-width: 480px) {
    .post-controls-row {
        flex-direction: column;
        align-items: stretch;
    }

    .control-btn {
        width: 100%;
        justify-content: center;
    }

    .audience-btn {
        justify-content: space-between;
    }
}

.no-permission-message {
    text-align: center;
    padding: 40px 20px;
    background: #f8f9fa;
    border-radius: 8px;
    color: #666;
    margin-bottom: 20px;
}

.post-attachments {
    margin: 10px 0;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 6px;
}

.post-attachments h5 {
    margin: 0 0 8px 0;
    font-size: 12px;
    color: #666;
}

.attachment-list {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.attachment-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px;
    background: white;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
}

.attachment-icon {
    font-size: 16px;
}

.attachment-name {
    flex: 1;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.attachment-actions {
    display: flex;
    gap: 3px;
}

.download-btn, .view-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    font-size: 12px;
}

.download-btn:hover {
    color: #28a745;
}

.view-btn:hover {
    color: #1dbcbc;
}

.post-audience {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #eee;
}

.post-audience small {
    color: #666;
    font-style: italic;
}

.post-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
}

.priority-select {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 8px;
    font-size: 14px;
}

.post-btn {
    background: #1dbcbc;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 10px 20px;
    cursor: pointer;
    font-weight: 500;
}

.post-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.post-btn:hover:not(:disabled) {
    background: #159999;
}

.posts-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.post-item {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 15px;
    border-left: 4px solid #ddd;
    cursor: pointer;
    transition: all 0.2s;
}

.post-item:hover {
    background: #f0f1f2;
}

.post-item.unread {
    border-left-color: #1dbcbc;
    background: #f0fffe;
}

.post-item.important {
    border-left-color: #ffa502;
}

.post-item.urgent {
    border-left-color: #ff4757;
    background: #fff5f5;
}

.post-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.author-name {
    font-weight: 600;
    color: #333;
}

.post-time {
    font-size: 12px;
    color: #666;
    margin-left: 10px;
}

.post-priority {
    font-size: 16px;
}

.post-content {
    color: #444;
    line-height: 1.4;
    margin-bottom: 10px;
    white-space: pre-wrap;
}

.post-footer {
    display: flex;
    justify-content: flex-end;
}

.delete-btn {
    background: none;
    border: none;
    color: #666;
    cursor: pointer;
    padding: 5px;
    font-size: 14px;
}

.delete-btn:hover {
    color: #ff4757;
}

.no-posts {
    text-align: center;
    color: #666;
    padding: 40px 20px;
}

.bulletin-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
}

@media (max-width: 768px) {
    .bulletin-board-panel {
        width: 100vw;
        right: -100vw;
    }
}
</style>
